import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, WhatsappLogo, Phone } from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/calculators", label: "Calculators" },
  { to: "/knowledge", label: "Knowledge" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/compliance-calendar", label: "Calendar" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${scrolled ? "bg-[var(--pta-cream)]/95 backdrop-blur-md border-b hairline" : "bg-[var(--pta-cream)] border-b hairline"}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
          <span className="w-8 h-8 border hairline-navy flex items-center justify-center font-serif-display text-[var(--pta-navy)] text-lg">P</span>
          <div className="flex flex-col leading-tight">
            <span className="font-serif-display text-[var(--pta-navy)] text-lg md:text-xl">Pankaj Talan</span>
            <span className="font-mono-fine text-[10px] tracking-[0.24em] uppercase text-[var(--pta-gold)]">& Advisory</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.slice(1, 8).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-mono-fine tracking-widest uppercase gold-underline ${isActive ? "text-[var(--pta-navy)]" : "text-[var(--pta-ink)]/80 hover:text-[var(--pta-navy)]"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://wa.me/919560029986"
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp"
            className="hidden md:inline-flex btn-pta-outline"
          >
            <WhatsappLogo size={16} weight="bold" /> WhatsApp
          </a>
          <Link to="/book" data-testid="header-book" className="hidden md:inline-flex btn-pta">
            Book Consultation
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 border hairline-navy"
            aria-label="menu"
          >
            {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[var(--pta-cream)] border-t hairline overflow-hidden"
          >
            <div className="px-6 py-6 grid grid-cols-2 gap-4">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm font-mono-fine tracking-widest uppercase text-[var(--pta-navy)] py-2"
                >
                  {n.label}
                </Link>
              ))}
              <Link to="/portal" data-testid="mobile-nav-portal" className="text-sm font-mono-fine tracking-widest uppercase text-[var(--pta-gold)] py-2">
                Client Portal
              </Link>
              <a href="tel:+919560029986" data-testid="mobile-call" className="text-sm font-mono-fine tracking-widest uppercase text-[var(--pta-navy)] py-2 flex items-center gap-2">
                <Phone size={14} /> 9560029986
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  return (
    <footer className="section-dark border-t hairline-gold mt-24" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="chapter-num">Chapter · End</div>
          <h3 className="font-serif-display text-4xl md:text-5xl mt-4 leading-[1.05] text-[var(--pta-cream)]">
            Finance.<br />Compliance.<br /><span className="text-[var(--pta-gold)]">Growth.</span>
          </h3>
          <p className="mt-6 text-white/70 max-w-md text-sm leading-relaxed">
            We don't just help businesses stay compliant — we help them become financially stronger, operationally efficient, and ready for sustainable growth.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/book" data-testid="footer-book" className="btn-pta-gold">Book Consultation</Link>
            <a href="https://wa.me/919560029986" target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="inline-flex items-center gap-2 px-6 py-3 border hairline-gold text-[var(--pta-gold)] font-mono-fine text-xs tracking-widest uppercase hover:bg-[var(--pta-gold)] hover:text-[var(--pta-navy)] transition-colors">
              <WhatsappLogo size={16} weight="bold" /> Chat on WhatsApp
            </a>
          </div>
        </div>
        <div className="md:col-span-3">
          <div className="overline text-[var(--pta-gold)]">Navigate</div>
          <ul className="mt-5 space-y-2 text-white/80 text-sm">
            {NAV.map((n) => (
              <li key={n.to}><Link data-testid={`footer-${n.label.toLowerCase().replace(/\s+/g,'-')}`} to={n.to} className="hover:text-[var(--pta-gold)] transition-colors">{n.label}</Link></li>
            ))}
            <li><Link data-testid="footer-portal" to="/portal" className="hover:text-[var(--pta-gold)] transition-colors">Client Portal</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="overline text-[var(--pta-gold)]">Contact</div>
          <div className="mt-5 space-y-3 text-white/80 text-sm">
            <div>
              <div className="font-mono-fine text-[11px] tracking-[0.2em] uppercase text-white/50">Phone</div>
              <a data-testid="footer-phone" href="tel:+919560029986" className="text-xl font-serif-display text-[var(--pta-cream)] hover:text-[var(--pta-gold)]">+91 9560 029 986</a>
            </div>
            <div>
              <div className="font-mono-fine text-[11px] tracking-[0.2em] uppercase text-white/50">Email</div>
              <a data-testid="footer-email" href="mailto:Pankaj.talan@MukMan.com" className="text-lg font-serif-display text-[var(--pta-cream)] hover:text-[var(--pta-gold)]">Pankaj.talan@MukMan.com</a>
            </div>
            <div>
              <div className="font-mono-fine text-[11px] tracking-[0.2em] uppercase text-white/50">Office</div>
              <div className="text-[var(--pta-cream)]">Delhi NCR, India</div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t hairline-gold/40" style={{ borderColor: 'rgba(212,175,55,0.25)' }}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-white/50 text-xs font-mono-fine tracking-widest uppercase">
          <div>© 2025—2026 CA Pankaj Talan And Advisory · All rights reserved</div>
          <div>Chartered Accountants · Big-Four Rigor · Advisory-Led</div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="grain min-h-screen">
      <Header />
      <main className="pt-16 md:pt-20">{children}</main>
      <Footer />
    </div>
  );
}
